import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Terminal, ArrowRight, Eye, EyeOff, ArrowLeft, KeyRound } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const HERO =
  "https://images.unsplash.com/photo-1482053450283-3e0b78b09a70?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyYWwlMjBzaGFwZXMlMjAzZHxlbnwwfHx8fDE3ODYxMzkyODl8MA&ixlib=rb-4.1.0&q=85";

export default function Auth({ mode }) {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // view: "login" | "register" | "forgot" | "reset"
  const [view, setView] = useState(mode === "register" ? "register" : "login");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  // reset flow
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newConfirm, setNewConfirm] = useState("");

  useEffect(() => {
    setView(mode === "register" ? "register" : "login");
  }, [mode]);

  useEffect(() => {
    const t = searchParams.get("reset_token");
    if (t) {
      setResetToken(t);
      setView("reset");
    }
  }, [searchParams]);

  useEffect(() => {
    if (user && user !== false) navigate("/");
  }, [user, navigate]);

  const submitAuth = async (e) => {
    e.preventDefault();
    if (view === "register" && password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      if (view === "login") await login(email, password);
      else await register(name, email, password);
      toast.success(view === "login" ? "Welcome back" : "Account created");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.reset_token) {
        setResetToken(data.reset_token);
        setView("reset");
        toast.success("Reset token generated — set a new password below.");
      } else {
        toast.success(data.message);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (newPassword !== newConfirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token: resetToken,
        new_password: newPassword,
      });
      toast.success(data.message || "Password updated");
      setPassword("");
      setNewPassword("");
      setNewConfirm("");
      setView("login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const headings = {
    login: { kicker: "// sign in", title: "Access your workspace" },
    register: { kicker: "// create account", title: "Start building free" },
    forgot: { kicker: "// account recovery", title: "Reset your password" },
    reset: { kicker: "// set new password", title: "Choose a new password" },
  };
  const h = headings[view];

  return (
    <div className="h-screen w-full flex bg-background text-foreground">
      {/* Left hero */}
      <div className="hidden lg:flex w-1/2 relative border-r border-border overflow-hidden">
        <img src={HERO} alt="architecture" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-background/70" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-2">
            <Terminal className="h-5 w-5 text-primary" />
            <span className="font-heading font-bold text-lg tracking-tight">FORGE</span>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">
              prompt · build · deploy
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter leading-[1.05]">
              Describe it. <br /> Ship it. <br />
              <span className="text-primary">In seconds.</span>
            </h1>
            <p className="mt-6 text-muted-foreground max-w-sm">
              The AI app builder that turns a sentence into a running, publishable application.
            </p>
          </div>
          <div className="font-mono text-xs text-muted-foreground">
            <span className="animate-blink">▊</span> claude-sonnet-4.6 engine
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Terminal className="h-5 w-5 text-primary" />
            <span className="font-heading font-bold text-lg tracking-tight">FORGE</span>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {h.kicker}
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-2 mb-8">
            {h.title}
          </h2>

          {/* LOGIN / REGISTER */}
          {(view === "login" || view === "register") && (
            <form onSubmit={submitAuth} className="space-y-4">
              {view === "register" && (
                <Field label="Name">
                  <input
                    data-testid="auth-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="auth-input"
                    placeholder="Ada Lovelace"
                  />
                </Field>
              )}
              <Field label="Email">
                <input
                  data-testid="auth-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="auth-input"
                  placeholder="you@studio.dev"
                />
              </Field>
              <Field label="Password">
                <PasswordInput
                  testid="auth-password-input"
                  value={password}
                  onChange={setPassword}
                  show={showPw}
                  toggle={() => setShowPw((s) => !s)}
                />
              </Field>
              {view === "register" && (
                <Field label="Repeat password">
                  <PasswordInput
                    testid="auth-confirm-input"
                    value={confirm}
                    onChange={setConfirm}
                    show={showPw}
                    toggle={() => setShowPw((s) => !s)}
                    placeholder="Re-enter password"
                  />
                </Field>
              )}

              {view === "login" && (
                <div className="flex justify-end -mt-1">
                  <button
                    type="button"
                    data-testid="forgot-password-link"
                    onClick={() => setView("forgot")}
                    className="font-mono text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <button
                data-testid="auth-submit-button"
                disabled={loading}
                className="w-full h-11 bg-primary text-primary-foreground font-mono text-sm uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {loading ? "processing..." : view === "login" ? "sign in" : "create account"}
                {!loading && <ArrowRight className="h-4 w-4" />}
              </button>

              <p className="text-sm text-muted-foreground">
                {view === "login" ? "No account yet? " : "Already registered? "}
                <Link
                  data-testid="auth-switch-link"
                  to={view === "login" ? "/register" : "/login"}
                  className="text-primary font-medium hover:underline"
                >
                  {view === "login" ? "Create one" : "Sign in"}
                </Link>
              </p>
            </form>
          )}

          {/* FORGOT */}
          {view === "forgot" && (
            <form onSubmit={submitForgot} className="space-y-4">
              <p className="text-sm text-muted-foreground -mt-4 mb-2">
                Enter your account email and we'll generate a reset token.
              </p>
              <Field label="Email">
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="auth-input"
                  placeholder="you@studio.dev"
                />
              </Field>
              <button
                data-testid="forgot-submit-button"
                disabled={loading}
                className="w-full h-11 bg-primary text-primary-foreground font-mono text-sm uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                <KeyRound className="h-4 w-4" />
                {loading ? "sending..." : "send reset token"}
              </button>
              <button
                type="button"
                data-testid="back-to-login-button"
                onClick={() => setView("login")}
                className="w-full h-10 flex items-center justify-center gap-2 font-mono text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </button>
            </form>
          )}

          {/* RESET */}
          {view === "reset" && (
            <form onSubmit={submitReset} className="space-y-4">
              <Field label="Reset token">
                <input
                  data-testid="reset-token-input"
                  value={resetToken}
                  onChange={(e) => setResetToken(e.target.value)}
                  required
                  className="auth-input font-mono text-xs"
                  placeholder="paste your reset token"
                />
              </Field>
              <Field label="New password">
                <PasswordInput
                  testid="reset-password-input"
                  value={newPassword}
                  onChange={setNewPassword}
                  show={showPw}
                  toggle={() => setShowPw((s) => !s)}
                />
              </Field>
              <Field label="Repeat new password">
                <PasswordInput
                  testid="reset-confirm-input"
                  value={newConfirm}
                  onChange={setNewConfirm}
                  show={showPw}
                  toggle={() => setShowPw((s) => !s)}
                  placeholder="Re-enter new password"
                />
              </Field>
              <button
                data-testid="reset-submit-button"
                disabled={loading}
                className="w-full h-11 bg-primary text-primary-foreground font-mono text-sm uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {loading ? "updating..." : "update password"}
                {!loading && <ArrowRight className="h-4 w-4" />}
              </button>
              <button
                type="button"
                data-testid="reset-back-button"
                onClick={() => setView("login")}
                className="w-full h-10 flex items-center justify-center gap-2 font-mono text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </button>
            </form>
          )}

          <style>{`
            .auth-input{width:100%;height:44px;padding:0 14px;background:hsl(var(--card));border:1px solid hsl(var(--border));border-radius:4px;font-size:14px;outline:none;transition:border-color .2s, box-shadow .2s;}
            .auth-input:focus{border-color:hsl(var(--primary));box-shadow:0 0 0 2px hsl(var(--primary));}
          `}</style>
        </div>
      </div>
    </div>
  );
}

function PasswordInput({ testid, value, onChange, show, toggle, placeholder = "••••••••" }) {
  return (
    <div className="relative">
      <input
        data-testid={testid}
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="auth-input pr-11"
        placeholder={placeholder}
      />
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        onClick={toggle}
        className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
